import { FC } from 'react';

import { CampaignProductType } from 'utils/constants/campaign';

import { Button, Card, Checkbox, Flex } from '@hh.ru/magritte-ui';
import { CopyOutlinedSize16, PenOutlinedSize16, PlusCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import {
    CampaignActionKey,
    CampaignActionsMenuItem,
    useCampaignActionsMenuItems,
} from 'components/CampaignsDashboard/hooks/useCampaignActionsMenuItems';
import { useCampaignRowModel } from 'components/CampaignsDashboard/hooks/useCampaignRowModel';
import { useCampaignSelection } from 'components/CampaignsDashboard/hooks/useCampaignSelection';
import useTrls from 'hooks/translation/useTrls';

import {
    CAMPAIGN_CARD_VISIBLE_COLUMNS,
    CAMPAIGNS_TABLE_COLUMNS_MAP,
    CampaignsTableColumn,
} from 'components/CampaignsDashboard/components/CampaignsTable/columns';
import { CampaignActionsBottomSheet } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignActions/CampaignActionsBottomSheet';
import { CampaignCardDetailsBottomSheet } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignCard/CampaignCardDetailsBottomSheet';
import { CampaignParameterRow } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignCard/CampaignParameterRow';
import { CampaignName } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignName/CampaignName';
import { CampaignSwitch } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignSwitch';
import { CampaignTableCell } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignTableCell';

import { CampaignFrontendStatus, DashboardCampaign } from 'store/models/campaignsDashboard';

const TrlKeys = {
    productType: 'CampaignsDashboard.CampaignsTable.mobile.column.productType',
};

interface CampaignCardProps {
    campaign: DashboardCampaign;
}

export const CampaignCard: FC<CampaignCardProps> = ({ campaign }) => {
    const trls = useTrls();
    const {
        campaignStatistics,
        campaignDetails,
        isStatisticsLoading,
        isDetailsLoading,
        isInsufficientBalance,
        onToggleStatus,
        isStatusToggleLoading,
    } = useCampaignRowModel(campaign);
    const { isSudo, actionsMenuItems, sudoActionsMenuItems, isMutatingActionAvailable } = useCampaignActionsMenuItems(
        campaign,
        onToggleStatus,
        isStatusToggleLoading
    );
    const { isChecked, isSelectionAvailable, onSelectionChange } = useCampaignSelection(
        campaign,
        isMutatingActionAvailable
    );

    const isArchived = campaign.frontendStatus === CampaignFrontendStatus.Archived;
    const visibleColumns = CAMPAIGN_CARD_VISIBLE_COLUMNS.filter(
        (column) => column !== CampaignsTableColumn.Banners || campaign.productType === CampaignProductType.Cpc
    );
    const increaseBudgetAction = actionsMenuItems.find(({ key }) => key === CampaignActionKey.IncreaseBudget);
    const editAction = actionsMenuItems.find(({ key }) => key === CampaignActionKey.Edit);
    const duplicateAction = actionsMenuItems.find(({ key }) => key === CampaignActionKey.Duplicate);
    const primaryAction = isArchived ? duplicateAction : increaseBudgetAction;

    const renderWideAction = (action?: CampaignActionsMenuItem) => {
        if (!action?.visible) return null;

        const { trlKey, disabled, to, onClick } = action;
        const icon =
            action.key === CampaignActionKey.IncreaseBudget ? <PlusCircleOutlinedSize16 /> : <CopyOutlinedSize16 />;
        const commonProps = {
            'data-qa': `campaign-mobile-primary-action-${action.key}-${campaign.id}`,
            stretched: true,
            spaceBetween: false,
            size: 'small' as const,
            mode: 'secondary' as const,
            style: 'neutral' as const,
            disabled,
            icon,
            onClick,
        };

        return to ? (
            <Button {...commonProps} Element={SPALink} to={to}>
                {trls(trlKey)}
            </Button>
        ) : (
            <Button {...commonProps}>{trls(trlKey)}</Button>
        );
    };
    const primaryActionContent = renderWideAction(primaryAction);

    return (
        <Card
            stretched
            direction="column"
            borderRadius={16}
            borderWidth="default"
            borderStyle="neutral"
            data-qa={`campaign-card-${campaign.id}`}
        >
            <Card.Segment p={16}>
                {!isArchived && (
                    <Flex align="flex-start" mb={12}>
                        {isSelectionAvailable && (
                            <Checkbox
                                checked={isChecked}
                                onChange={onSelectionChange}
                                data-qa={`campaign-mobile-checkbox-${campaign.id}`}
                            />
                        )}
                        <Flex ml="auto">
                            <CampaignSwitch
                                campaign={campaign}
                                isStatusToggleLoading={isStatusToggleLoading}
                                onToggleStatus={onToggleStatus}
                            />
                        </Flex>
                    </Flex>
                )}
                <CampaignName campaign={campaign} isInsufficientBalance={isInsufficientBalance} isMobile />
            </Card.Segment>

            <Card.Between />

            <Card.Segment p={16} gap={8}>
                {visibleColumns.map((column) => (
                    <CampaignParameterRow
                        key={column}
                        label={trls(
                            column === CampaignsTableColumn.ProductType
                                ? TrlKeys.productType
                                : CAMPAIGNS_TABLE_COLUMNS_MAP[column].trlKey
                        )}
                    >
                        <CampaignTableCell
                            columnKey={column}
                            campaign={campaign}
                            campaignStatistics={campaignStatistics}
                            campaignDetails={campaignDetails}
                            isStatisticsLoading={isStatisticsLoading}
                            isDetailsLoading={isDetailsLoading}
                            isStatusToggleLoading={isStatusToggleLoading}
                            onToggleStatus={onToggleStatus}
                            isInsufficientBalance={isInsufficientBalance}
                            isMobile
                        />
                    </CampaignParameterRow>
                ))}
                <CampaignCardDetailsBottomSheet
                    campaign={campaign}
                    campaignStatistics={campaignStatistics}
                    campaignDetails={campaignDetails}
                    isStatisticsLoading={isStatisticsLoading}
                    isDetailsLoading={isDetailsLoading}
                    isStatusToggleLoading={isStatusToggleLoading}
                    onToggleStatus={onToggleStatus}
                    isInsufficientBalance={isInsufficientBalance}
                />
            </Card.Segment>

            <Card.Between />

            <Card.Segment direction="row" justify={primaryActionContent ? undefined : 'flex-end'} gap={8} p={12}>
                {primaryActionContent && (
                    <Flex grow={1} minWidth={0}>
                        {primaryActionContent}
                    </Flex>
                )}
                {!isArchived && editAction?.visible && editAction.to && (
                    <Button
                        Element={SPALink}
                        to={editAction.to}
                        size="small"
                        mode="secondary"
                        style="neutral"
                        icon={<PenOutlinedSize16 />}
                        hideLabel
                        aria-label={trls(editAction.trlKey)}
                        data-qa={`campaign-mobile-edit-${campaign.id}`}
                    />
                )}
                <CampaignActionsBottomSheet
                    campaign={campaign}
                    isSudo={isSudo}
                    actionsMenuItems={actionsMenuItems}
                    sudoActionsMenuItems={sudoActionsMenuItems}
                />
            </Card.Segment>
        </Card>
    );
};
